import { AccessProfile } from '../models/user.model';

function getAccessProfile(claims: Record<string, unknown> | null): unknown {
  return claims?.['accessProfile'];
}

export function isAdminRole(claims: Record<string, unknown> | null): boolean {
  return getAccessProfile(claims) === AccessProfile.Administrador;
}

export function isProjectManagerRole(claims: Record<string, unknown> | null): boolean {
  return getAccessProfile(claims) === AccessProfile.GerenteDeProjetos;
}

export function isPeopleManagementRole(claims: Record<string, unknown> | null): boolean {
  return getAccessProfile(claims) === AccessProfile.GestaoDePessoas;
}

export function isAdminOrManager(claims: Record<string, unknown> | null): boolean {
  return isAdminRole(claims) || isProjectManagerRole(claims);
}

export function isCollaboratorRole(claims: Record<string, unknown> | null): boolean {
  return getAccessProfile(claims) === AccessProfile.Colaborador;
}

export function canAccessApp(claims: Record<string, unknown> | null): boolean {
  return isAdminOrManager(claims) || isPeopleManagementRole(claims);
}

export function canAccessMainApp(claims: Record<string, unknown> | null): boolean {
  return canAccessApp(claims);
}

export function canAccessTimeTrackingModule(claims: Record<string, unknown> | null): boolean {
  return (
    isCollaboratorRole(claims) ||
    isProjectManagerRole(claims) ||
    isPeopleManagementRole(claims) ||
    isAdminRole(claims)
  );
}

export function canAccessOneOnOneModule(claims: Record<string, unknown> | null): boolean {
  return (
    isCollaboratorRole(claims) ||
    isProjectManagerRole(claims) ||
    isPeopleManagementRole(claims) ||
    isAdminRole(claims)
  );
}

export function canAccessOmbudsmanModule(claims: Record<string, unknown> | null): boolean {
  return (
    isCollaboratorRole(claims) ||
    isProjectManagerRole(claims) ||
    isPeopleManagementRole(claims) ||
    isAdminRole(claims)
  );
}

export function canManageOmbudsmanSettings(claims: Record<string, unknown> | null): boolean {
  return isAdminRole(claims);
}

export function canManageOneOnOnes(claims: Record<string, unknown> | null): boolean {
  return isAdminRole(claims) || isPeopleManagementRole(claims);
}

export function canManageTimeTracking(claims: Record<string, unknown> | null): boolean {
  return isAdminRole(claims) || isPeopleManagementRole(claims);
}

export function canManageRecruitment(claims: Record<string, unknown> | null): boolean {
  return isAdminRole(claims) || isPeopleManagementRole(claims);
}

export function canReviewTimeTracking(claims: Record<string, unknown> | null): boolean {
  return canManageTimeTracking(claims);
}

export function canManagePaidLeave(claims: Record<string, unknown> | null): boolean {
  return isAdminRole(claims) || isPeopleManagementRole(claims);
}

export function canSelfServiceTimeTracking(claims: Record<string, unknown> | null): boolean {
  return canAccessTimeTrackingModule(claims);
}

export function canManageOperationalData(claims: Record<string, unknown> | null): boolean {
  return isAdminOrManager(claims);
}

export function canManageProjects(claims: Record<string, unknown> | null): boolean {
  return isAdminOrManager(claims);
}

export function canManageProjectPhaseConfig(claims: Record<string, unknown> | null): boolean {
  return isAdminRole(claims);
}

export function canManageClients(claims: Record<string, unknown> | null): boolean {
  return isAdminOrManager(claims);
}

export function canManageUsers(claims: Record<string, unknown> | null): boolean {
  return isAdminRole(claims) || isPeopleManagementRole(claims);
}

export function canReadRoles(claims: Record<string, unknown> | null): boolean {
  return isAdminRole(claims) || isPeopleManagementRole(claims);
}

export function canViewCosts(claims: Record<string, unknown> | null): boolean {
  return isAdminRole(claims);
}

export function canViewUserCosts(claims: Record<string, unknown> | null): boolean {
  return canManageUsers(claims);
}

export function canAssignAccessProfile(
  claims: Record<string, unknown> | null,
  accessProfile: AccessProfile
): boolean {
  if (isAdminRole(claims)) {
    return true;
  }

  return isPeopleManagementRole(claims) && accessProfile !== AccessProfile.Administrador;
}

export function canManageAccessProfile(
  claims: Record<string, unknown> | null,
  accessProfile: AccessProfile | null | undefined
): boolean {
  if (!accessProfile) {
    return canManageUsers(claims);
  }

  return canAssignAccessProfile(claims, accessProfile);
}
